import { FC, useEffect, useRef, useState } from 'react';

import { Button, NumberInput } from '@smartshell/ui-kit';

import s from './EditableField.module.scss';

interface IEditableFieldProps {
  defaultValue: number | '';
  onSave: (value: number | '') => Promise<unknown>;
  validate: (value: number | '') => string | undefined;
  sign?: string;
  disabled?: boolean;
}

export const EditableField: FC<IEditableFieldProps> = ({
  defaultValue,
  onSave,
  validate,
  sign,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (!isEditMode) return;

    inputRef.current?.focus();
  }, [isEditMode]);

  const submit = (): void => {
    if (error) return;

    if (value === defaultValue) {
      return setIsEditMode(false);
    }

    setIsSaving(true);

    onSave(value)
      .then(() => {
        setIsEditMode(false);
      })
      .catch(console.error)
      .finally(() => setIsSaving(false));
  };

  return (
    <div
      className={s.container}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();

          setValue(defaultValue);
          setIsEditMode(false);
        }

        if (e.key === 'Enter') {
          e.stopPropagation();
          e.preventDefault();

          submit();
        }
      }}
    >
      <NumberInput
        ref={inputRef}
        value={value}
        onChange={(newValue) => {
          setError(validate(newValue));
          setValue(newValue);
        }}
        variant="secondary"
        error={error}
        rightContent={sign}
        disabled={!isEditMode || isSaving}
        bordered
      />
      <Button
        variant="secondary"
        onClick={(e) => {
          e.stopPropagation();

          if (!isEditMode) {
            return setIsEditMode(true);
          }

          submit();
        }}
        disabled={isSaving || disabled}
        className={s.button}
        iconName={isEditMode ? 'CheckIcon' : 'EditIcon'}
      />
    </div>
  );
};
